import mixpanel from 'mixpanel-browser'

const MIXPANEL_TOKEN = import.meta.env.VITE_MIXPANEL_TOKEN

let isInitialized = false

/**
 * Initialize Mixpanel analytics
 */
export function initAnalytics(): void {
  if (!MIXPANEL_TOKEN) {
    // Use console.warn directly here to avoid circular dependency with logger
    console.warn('[Mixpanel] Token not configured, analytics disabled')
    return
  }

  if (isInitialized) return

  mixpanel.init(MIXPANEL_TOKEN, {
    track_pageview: true,
    persistence: 'localStorage',
    ignore_dnt: false,
  })

  isInitialized = true
}

/**
 * Track a custom event
 */
export function track(event: string, properties?: Record<string, unknown>): void {
  if (!isInitialized) return
  mixpanel.track(event, properties)
}

/**
 * Identify a user
 */
export function identify(userId: string): void {
  if (!isInitialized) return
  mixpanel.identify(userId)
}

/**
 * Set user properties
 */
export function setUserProperties(properties: Record<string, unknown>): void {
  if (!isInitialized) return
  mixpanel.people.set(properties)
}

/**
 * Reset analytics (on sign out)
 */
export function reset(): void {
  if (!isInitialized) return
  mixpanel.reset()
}

// Event constants for type safety
export const AnalyticsEvents = {
  // Notes
  NOTE_CREATED: 'note_created',
  NOTE_OPENED: 'note_opened',
  NOTE_DELETED: 'note_deleted',
  ALL_NOTES_DELETED: 'all_notes_deleted',

  // Command palette
  COMMAND_PALETTE_OPENED: 'command_palette_opened',
  COMMAND_PALETTE_ACTION_EXECUTED: 'command_palette_action_executed',

  // Editor
  SLASH_COMMAND_USED: 'slash_command_used',
  IMAGE_PASTED: 'image_pasted',
  IMAGE_UPLOAD_FAILED: 'image_upload_failed',

  // Notes
  NOTE_PINNED: 'note_pinned',
  NOTE_UNPINNED: 'note_unpinned',
  SEARCH_EXECUTED: 'search_executed',

  // Sync
  SYNC_TRIGGERED: 'sync_triggered',
  SYNC_COMPLETED: 'sync_completed',
  SYNC_FAILED: 'sync_failed',

  // Auth
  SIGN_IN_STARTED: 'sign_in_started',
  SIGN_IN_COMPLETED: 'sign_in_completed',
  SIGN_UP_COMPLETED: 'sign_up_completed',
  SIGN_OUT: 'sign_out',

  // UI
  THEME_TOGGLED: 'theme_toggled',

  // Payments
  UPGRADE_CLICKED: 'upgrade_clicked',
  SUBSCRIPTION_COMPLETED: 'subscription_completed',
  SUBSCRIPTION_SCREEN_VIEWED: 'subscription_screen_viewed',
  SUBSCRIPTION_CANCELLED: 'subscription_cancelled',
  SUPPORT_CLICKED: 'support_clicked',

  // Settings
  SETTINGS_OPENED: 'settings_opened',
  PASSWORD_CHANGED: 'password_changed',
} as const
